import { z } from "zod";

export const AD_LAUNCHER_MULTI_ACCOUNT_FLAG = "ad_launcher_multi_account";
export type LauncherEnvironment = "development" | "production" | null;
export function launcherEnvironment(nodeEnv?: string, deployment?: string): LauncherEnvironment {
  if (nodeEnv === "production" || deployment) return "production";
  return nodeEnv === "development" ? "development" : null;
}
export function multiAccountLauncherEnabled(settings: unknown, environment: LauncherEnvironment): boolean {
  return !!environment && (settings as any)?.scopedFeatures?.[AD_LAUNCHER_MULTI_ACCOUNT_FLAG]?.[environment] === true;
}

export const numericLauncherId = z.string().trim().regex(/^\d{1,64}$/, "Use a numeric identifier");
export const launchPageSchema = z.object({
  pageId: numericLauncherId,
  name: z.string().trim().min(1).max(160),
  instagramActorId: numericLauncherId.optional(),
  isDefault: z.boolean(),
}).strict();
export const launchPagesSchema = z.array(launchPageSchema).min(1).max(50).superRefine((pages, ctx) => {
  if (pages.filter(page => page.isDefault).length !== 1) {
    ctx.addIssue({ code: "custom", message: "Choose exactly one default page" });
  }
  if (new Set(pages.map(page => page.pageId)).size !== pages.length) {
    ctx.addIssue({ code: "custom", message: "Page identifiers must be unique" });
  }
});
export type LaunchPage = z.infer<typeof launchPageSchema>;

/** Discovery is not membership or proof of Meta advertising permission. */
export type WindsorOrganicPage = {
  pageId: string;
  name: string;
  source: "windsor_facebook_organic";
};
export type DiscoveredLaunchPage = {
  pageId: string;
  name: string;
  sources: Array<"meta_ads" | "windsor_facebook_organic" | "account_history" | "business_settings">;
};
/** Operator-supplied Business Settings evidence, not an account association. */
export type ConfirmedLaunchPage = { pageId: string; name: string; source: "business_settings" };
export type ObservedLaunchPage = { pageId: string; name: string };
export type AccountPageDiscovery = {
  accountId: string;
  pages: ObservedLaunchPage[];
  windowDays: number;
};

/** actor_id is Page evidence; account_id and account names are never Page identities. */
export function observedLaunchPages(rows: readonly Record<string, unknown>[], accountId: string): ObservedLaunchPage[] {
  const pages = new Map<string, ObservedLaunchPage>();
  for (const row of rows) {
    if (String(row.account_id ?? "").replace(/^act_/i, "") !== accountId) continue;
    const raw = row.actor_id;
    if (typeof raw !== "string" && !(typeof raw === "number" && Number.isSafeInteger(raw))) continue;
    const pageId = String(raw).trim();
    if (!numericLauncherId.safeParse(pageId).success || /^0+$/.test(pageId)) continue;
    // Windsor Ads has no verified Page-name field. Connected sources can enrich
    // this exact ID later; never substitute the paying account's name.
    if (!pages.has(pageId)) pages.set(pageId, { pageId, name: pageId });
  }
  return [...pages.values()];
}
export function mergeDiscoveredLaunchPages(
  metaPages: readonly { id?: string; name?: string }[],
  organicPages: readonly WindsorOrganicPage[],
  accountPages: readonly ObservedLaunchPage[] = [],
  confirmedPages: readonly ConfirmedLaunchPage[] = [],
): DiscoveredLaunchPage[] {
  const pages = new Map<string, DiscoveredLaunchPage>();
  const add = (pageId: string, name: string, source: DiscoveredLaunchPage["sources"][number]) => {
    if (!numericLauncherId.safeParse(pageId).success) return;
    const existing = pages.get(pageId);
    if (existing) {
      if (!existing.sources.includes(source)) existing.sources.push(source);
      if (existing.name === pageId && name) existing.name = name;
    } else pages.set(pageId, { pageId, name: name || pageId, sources: [source] });
  };
  metaPages.forEach(page => add(page.id ?? "", page.name?.trim() ?? "", "meta_ads"));
  organicPages.forEach(page => add(page.pageId, page.name, page.source));
  confirmedPages.forEach(page => add(page.pageId, page.name, page.source));
  accountPages.forEach(page => add(page.pageId, page.name, "account_history"));
  return [...pages.values()];
}

/** Display/read projection only. Server selection still validates membership. */
export function launchAccountWithSelectedPage<T extends {
  pageId: string | null;
  pages?: { pageId: string; isDefault: boolean }[];
}>(account: T | null, requestedPageId: string): T | null {
  if (!account) return null;
  const page = account.pages?.find(item => item.pageId === requestedPageId)
    ?? account.pages?.find(item => item.isDefault);
  return page && page.pageId !== account.pageId ? { ...account, pageId: page.pageId } : account;
}
export type LaunchIdentity = {
  accountRowId: string; accountId: string; accountName: string;
  pageId: string; pageName: string; instagramActorId?: string;
};
export type SelectableLaunchAccount = {
  id: string; brandId: string; accountId: string; name: string;
  pageId: string | null; pagesJson?: LaunchPage[] | null; archivedAt?: Date | string | null;
};
export class LaunchSelectionError extends Error {
  readonly status = 400;
}
export function normalizeLaunchAccountId(value: unknown): string {
  return numericLauncherId.parse(typeof value === "string" ? value.trim().replace(/^act_/i, "") : value);
}
export function launchAccountPages(account: Pick<SelectableLaunchAccount, "pageId" | "pagesJson">): LaunchPage[] {
  if (account.pagesJson != null) return launchPagesSchema.parse(account.pagesJson);
  return account.pageId ? [{ pageId: account.pageId, name: account.pageId, isDefault: true }] : [];
}
/** Validate the complete pair before doing any provider work or persistence. */
export function selectLaunchAccount<T extends SelectableLaunchAccount>(input: {
  brandId: string; accounts: readonly T[]; connectedAccountId: string | null;
  accountRowId?: unknown; pageId?: unknown;
}): { account: T | null; page: LaunchPage | null } {
  const explicit = input.accountRowId != null && input.accountRowId !== "";
  const account = input.accounts.find(row => row.brandId === input.brandId && !row.archivedAt
    && (explicit ? row.id === input.accountRowId : row.accountId === input.connectedAccountId)) ?? null;
  if (explicit && !account) throw new LaunchSelectionError("Choose an active launch account belonging to this brand");
  if (!account) {
    if (input.pageId) throw new LaunchSelectionError("Choose an account before choosing a page");
    return { account: null, page: null };
  }
  const pages = launchAccountPages(account);
  const page = input.pageId ? pages.find(page => page.pageId === input.pageId) : pages.find(page => page.isDefault);
  if (input.pageId && !page) throw new LaunchSelectionError("Choose a page configured for the selected account");
  return { account, page: page ?? null };
}
/** All writers use the same atomic default synchronization, including legacy page edits. */
export function launchPagePatch(existing: { pageId: string | null; pagesJson?: LaunchPage[] | null }, body: any) {
  const requestedPageId = body.pageId === undefined ? undefined
    : body.pageId === null || body.pageId === "" ? null : numericLauncherId.parse(body.pageId);
  let pagesJson = existing.pagesJson ?? null;
  let pageId = existing.pageId;
  if (body.pagesJson !== undefined) {
    pagesJson = body.pagesJson === null ? null : launchPagesSchema.parse(body.pagesJson);
    if (pagesJson) {
      pageId = pagesJson.find(page => page.isDefault)!.pageId;
      if (requestedPageId !== undefined && requestedPageId !== pageId) {
        throw new LaunchSelectionError("Page ID must match the default configured page");
      }
    }
  }
  if (requestedPageId !== undefined && !pagesJson) {
    pageId = requestedPageId;
  } else if (requestedPageId !== undefined && pagesJson && body.pagesJson === undefined) {
    const chosen = pagesJson.find(page => page.pageId === requestedPageId);
    if (!chosen) throw new LaunchSelectionError("Page ID must belong to the configured pages");
    pageId = chosen.pageId;
    pagesJson = pagesJson.map(page => ({ ...page, isDefault: page.pageId === pageId }));
  }
  return { pageId, pagesJson };
}
